import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { calculateOrdersSummary, generateSummaryText, generateFirstFollowUpText, generateFinalNoticeText, generateFinalNoticeUltimateText } from '../../utils/orderUtils';
import { formatDate } from '../../utils/dateUtils';
import { formatAmountRound } from '../../utils/formatAmount';
import useManageOrder from '../order/useManageOrder';
import useManageDebtFollowUp from './useManageDebtFollowUp';
import CustomerPaymentHistoryModal from '../customerProfile/CustomerPaymentHistoryModal';
import DebtLetterModal from './DebtLetterModal';
import useBankTransferOnly from '../../hooks/useBankTransferOnly';
import { COMPANY_BANK_NOTICE, companyBankText } from '../../utils/companyBank';

const womenList = ['Eve', 'Meen', 'Phat', 'Kluay', 'Pla'];

const DebtFollowUp = () => {
  const { authState } = useAuth();
  const shopName = useSelector((state) => state.shop.name);
  const [activeTab, setActiveTab] = useState(1);
  const [copiedContacts, setCopiedContacts] = useState({});
  const [copyInProgress, setCopyInProgress] = useState({});

  // บันทึกนัด state (Tab 2 & 3)
  const [promiseDates, setPromiseDates] = useState({});
  const [promiseNotes, setPromiseNotes] = useState({});
  const [promiseSaving, setPromiseSaving] = useState({});
  const [noteOpen, setNoteOpen] = useState({});

  // Tab 4: tracking number per customerId
  const [trackingNumbers, setTrackingNumbers] = useState({});
  // Tab 5: date picker per callFollowUpId
  const [callDates, setCallDates] = useState({});
  // Tab 5: postpone date picker per callFollowUpId
  const [postponeDates, setPostponeDates] = useState({});
  const [postponeSaving, setPostponeSaving] = useState({});

  // Payment history modal
  const [paymentHistoryProfile, setPaymentHistoryProfile] = useState(null);

  // Debt letter modal
  const [letterModalOpen, setLetterModalOpen] = useState(false);
  const [letterData, setLetterData] = useState(null);
  const [letterLoading, setLetterLoading] = useState({});

  const {
    customerGroups, isLoading, tabCounts,
    fetchData, fetchCallData, fetchWaitingData, fetchTabCounts,
    markDone, markCall, markPromise, pushToFinalNotice,
    moveToWaiting, postponeCall, fetchLetterData, fetchBankInfo,
  } = useManageDebtFollowUp();
  const { createFirstOrderPaymentLink } = useManageOrder();
  const bankTransferOnly = useBankTransferOnly();

  const gender = womenList.includes(authState?.user?.nickname) ? 'คะ' : 'ครับ';
  const isTama = shopName === 'tama';

  const tabs = [
    { id: 1, label: 'ทวงครั้งที่ 2', count: tabCounts.tab1 },
    { id: 2, label: 'ทวงครั้งที่ 3', count: tabCounts.tab2 },
    { id: 3, label: 'Final Notice', count: tabCounts.tab3 },
    { id: 4, label: 'ส่งเอกสาร', count: tabCounts.tab4 },
    { id: 5, label: 'โทรตาม', count: tabCounts.tab5 },
    { id: 6, label: 'โทรตาม (รอติดตามผล)', count: tabCounts.tab6 },
  ];

  const loadTab = (tabId) => {
    if (tabId === 5) fetchCallData();
    else if (tabId === 6) fetchWaitingData();
    else fetchData(tabId);
  };

  useEffect(() => {
    setCopiedContacts({});
    setTrackingNumbers({});
    setCallDates({});
    setPostponeDates({});
    setPromiseDates({});
    setPromiseNotes({});
    setNoteOpen({});
    loadTab(activeTab);
    fetchTabCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, shopName]);

  const refreshAll = () => {
    loadTab(activeTab);
    fetchTabCounts();
  };

  // ─── Copy handler (Tab 1, 2, 3, 4) ──────────────────────────────────────────
  const handleCopy = async (group) => {
    if (copyInProgress[group.customerId]) return;
    setCopyInProgress(prev => ({ ...prev, [group.customerId]: true }));

    const { summary, grandTotal } = calculateOrdersSummary(group.orders, { ignoreDownPayment: true });
    const urls = (group.orders || []).map(o => o.url).filter(Boolean);

    let paymentSuffix = '';
    // While links are off, company customers get the company account instead of a link
    const usesPaymentLink = group.isCompanyCustomer && !bankTransferOnly;

    if (usesPaymentLink) {
      // Company customer: generate a fresh payment link (service cancels previous links automatically)
      try {
        const response = await createFirstOrderPaymentLink(
          { urls, smileName: group.smileName, paymentMethod: 'qrCode' },
          shopName
        );
        if (response?.success && response?.data?.paymentUrl) {
          const { paymentUrl, isCreditCard } = response.data;
          if (isCreditCard) {
            const originalAmount = parseFloat(String(grandTotal).replace(/[^\d.]/g, ''));
            const feeAmount = originalAmount * 0.035;
            paymentSuffix = `\n\n🔗 ชำระเงินได้ที่ลิงก์นี้${gender}:\n${paymentUrl}\n(ยอดรวม + ค่าธรรมเนียม 3.5% = ${(originalAmount + feeAmount).toFixed(2)} บาท)`;
          } else {
            paymentSuffix = `\n\n🔗 ชำระเงินได้ที่ลิงก์นี้${gender}:\n${paymentUrl}`;
          }
        } else {
          toast.error('สร้างลิงก์ชำระเงินไม่สำเร็จ ติดต่อ support');
          setCopyInProgress(prev => ({ ...prev, [group.customerId]: false }));
          return;
        }
      } catch (e) {
        toast.error('สร้างลิงก์ชำระเงินไม่สำเร็จ ติดต่อ support');
        setCopyInProgress(prev => ({ ...prev, [group.customerId]: false }));
        return;
      }
    } else if (group.isCompanyCustomer) {
      paymentSuffix = `\n\n${companyBankText(gender)}`;
      toast.warning(COMPANY_BANK_NOTICE);
    } else {
      // Non-company customer: show bank transfer info instead
      const totalBaht = parseFloat(String(grandTotal).replace(/[^\d.]/g, '')) || 0;
      const bankInfo = await fetchBankInfo(totalBaht);
      if (bankInfo) {
        paymentSuffix = `\n\nชำระผ่านโอนบัญชีธนาคาร${gender}\n${bankInfo.bankName}\nเลขบัญชี ${bankInfo.accountNumber}\nชื่อบัญชี ${bankInfo.accountNameTh}\n\nหลังชำระแล้วรบกวนส่งสลิปยืนยันด้วยนะ${gender}`;
      } else {
        toast.error('ดึงข้อมูลบัญชีธนาคารไม่สำเร็จ ติดต่อ support');
        setCopyInProgress(prev => ({ ...prev, [group.customerId]: false }));
        return;
      }
    }

    let text;
    if (activeTab === 1) {
      text = generateFirstFollowUpText(summary, grandTotal, gender);
    } else if (activeTab === 2) {
      text = generateFinalNoticeText(summary, grandTotal, gender, group.advanceAmount || 0);
    } else if (activeTab === 3) {
      // Final Notice: paymentSuffix is embedded by passing paymentUrl inline only for company customers
      text = usesPaymentLink
        ? generateFinalNoticeUltimateText(summary, grandTotal, gender, group.advanceAmount || 0, paymentSuffix.match(/https?:\/\/\S+/)?.[0] || null)
        : generateFinalNoticeUltimateText(summary, grandTotal, gender, group.advanceAmount || 0, null) + paymentSuffix;
    } else {
      text = generateSummaryText(summary, grandTotal, gender, isTama);
    }

    if (activeTab !== 3) {
      text += paymentSuffix;
    }

    try {
      await navigator.clipboard.writeText(text);
      toast.success(`คัดลอกข้อความสำหรับ ${group.contact} แล้ว`);
      setCopiedContacts(prev => ({ ...prev, [group.customerId]: true }));
    } catch (e) {
      toast.error('ไม่สามารถคัดลอกได้');
    } finally {
      setCopyInProgress(prev => ({ ...prev, [group.customerId]: false }));
    }
  };

  // ─── Tab 1–3 handlers ─────────────────────────────────────────────────────
  const handleDone = async (group) => {
    const orderIds = activeTab === 1 ? group.orders.map(o => o.id) : undefined;
    const trackingNumber = activeTab === 4 ? (trackingNumbers[group.customerId] || null) : null;
    const result = await markDone(group.customerId, activeTab, authState?.user?.id, orderIds, trackingNumber);
    if (result) {
      if (activeTab === 1) {
        toast.success(`เสร็จสิ้นสำหรับ ${group.contact} — หาก ลค ตอบกลับมา ไปกด "บันทึกนัด" ในหน้า "ทวงครั้งที่ 3" ได้เลย`);
      } else {
        toast.success(`เสร็จสิ้นสำหรับ ${group.contact}`);
      }
      refreshAll();
      setCopiedContacts(prev => { const n = { ...prev }; delete n[group.customerId]; return n; });
    } else {
      toast.error('เกิดข้อผิดพลาด');
    }
  };

  const handleMarkCall = async (group) => {
    const result = await markCall(group.customerId, authState?.user?.id);
    if (result) {
      toast.success(`ย้าย ${group.contact} ไปที่ Tab โทรตามแล้ว`);
      refreshAll();
    } else {
      toast.error('เกิดข้อผิดพลาด');
    }
  };

  const handleMarkPromise = async (group) => {
    const date = promiseDates[group.customerId];
    if (!date) {
      toast.error('กรุณาเลือกวันที่ลูกค้านัดชำระก่อน');
      return;
    }
    setPromiseSaving(prev => ({ ...prev, [group.customerId]: true }));
    const note = promiseNotes[group.customerId] || '';
    const orderIds = group.orders.map(o => o.id);
    const result = await markPromise(
      group.customerId, authState?.user?.id,
      date, note, activeTab, orderIds,
    );
    setPromiseSaving(prev => ({ ...prev, [group.customerId]: false }));
    if (result) {
      toast.success(`บันทึกนัดชำระสำหรับ ${group.contact} แล้ว`);
      refreshAll();
    } else {
      toast.error('เกิดข้อผิดพลาด');
    }
  };

  const handlePushToFinalNotice = async (group, targetStep = 2) => {
    const result = await pushToFinalNotice(
      group.callFollowUpId, group.customerId, authState?.user?.id, targetStep,
    );
    if (result) {
      const label = targetStep === 3 ? 'ส่งจดหมาย (Tab ส่งเอกสาร)' : 'Final Notice';
      toast.success(`ย้าย ${group.contact} ไป ${label} แล้ว`);
      refreshAll();
    } else {
      toast.error('เกิดข้อผิดพลาด');
    }
  };

  // ─── Tab 5 handler ────────────────────────────────────────────────────────
  const handleMoveToWaiting = async (group) => {
    const date = callDates[group.callFollowUpId];
    if (!date) {
      toast.error('กรุณาเลือกวันที่ลูกค้านัดชำระก่อน');
      return;
    }
    const result = await moveToWaiting(group.callFollowUpId, date);
    if (result) {
      toast.success(`บันทึกและย้าย ${group.contact} ไป "รอติดตามผล" แล้ว`);
      refreshAll();
    } else {
      toast.error('เกิดข้อผิดพลาด');
    }
  };

  // ─── Tab 5 handler ────────────────────────────────────────────────────────
  const handlePostpone = async (group) => {
    const date = postponeDates[group.callFollowUpId];
    if (!date) {
      toast.error('กรุณาเลือกวันที่ใหม่ก่อน');
      return;
    }
    setPostponeSaving(prev => ({ ...prev, [group.callFollowUpId]: true }));
    const result = await postponeCall(group.callFollowUpId, date);
    setPostponeSaving(prev => ({ ...prev, [group.callFollowUpId]: false }));
    if (result) {
      toast.success(`บันทึกเลื่อนชำระสำหรับ ${group.contact} แล้ว`);
      setPostponeDates(prev => { const n = { ...prev }; delete n[group.callFollowUpId]; return n; });
      refreshAll();
    } else {
      toast.error('เกิดข้อผิดพลาด');
    }
  };

  const handleOpenLetter = async (group) => {
    setLetterLoading(prev => ({ ...prev, [group.customerId]: true }));
    const data = await fetchLetterData(group.customerId);
    setLetterLoading(prev => ({ ...prev, [group.customerId]: false }));
    if (!data) {
      toast.error('ไม่สามารถโหลดข้อมูลได้');
      return;
    }
    setLetterData(data);
    setLetterModalOpen(true);
  };

  const calculateGroupTotal = (orders) => {
    const { grandTotal } = calculateOrdersSummary(orders);
    return grandTotal;
  };

  // ─── Advance amount display (Tabs 4 & 5) ─────────────────────────────────
  const renderAdvanceAmount = (grandTotal, advanceAmount) => {
    if (!advanceAmount || advanceAmount <= 0) return null;
    const netAmount = Math.max(0, grandTotal - advanceAmount);
    return (
      <div className="px-5 py-2 bg-blue-50 border-b border-blue-100 text-sm">
        <div className="flex flex-wrap gap-x-4 gap-y-1 items-center">
          <span className="text-gray-700">💰 ยอดรวม <span className="font-semibold">{grandTotal.toFixed(2)} บาท</span></span>
          <span className="text-red-600">➖ มียอดฝากไว้ <span className="font-semibold">{advanceAmount.toFixed(2)} บาท</span></span>
          <span className="text-green-700 font-bold">ยอดที่ต้องชำระ: {netAmount.toFixed(2)} บาท</span>
        </div>
      </div>
    );
  };

  // ─── บันทึกนัดชำระ section (Tab 2 & 3) ──────────────────────────────────────
  const renderPromiseSection = (group) => (
    <div className="px-5 py-3 bg-yellow-50 border-t border-yellow-200">
      <p className="text-sm font-semibold text-yellow-800 mb-2">📅 ลูกค้านัดชำระ — บันทึกวันนัดและ Note</p>
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-600">วันที่นัดชำระ <span className="text-red-500">*</span></label>
          <input
            type="date"
            className="border border-gray-300 rounded px-2 py-1 text-sm"
            value={promiseDates[group.customerId] || ''}
            onChange={(e) => setPromiseDates(prev => ({ ...prev, [group.customerId]: e.target.value }))}
          />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
          <label className="text-xs text-gray-600">Note (ถ้ามี)</label>
          <input
            type="text"
            placeholder="เช่น รอเงินเดือน, จะโอนวันเสาร์..."
            className="border border-gray-300 rounded px-2 py-1 text-sm"
            value={promiseNotes[group.customerId] || ''}
            onChange={(e) => setPromiseNotes(prev => ({ ...prev, [group.customerId]: e.target.value }))}
          />
        </div>
        <button
          onClick={() => handleMarkPromise(group)}
          disabled={!promiseDates[group.customerId] || !!promiseSaving[group.customerId]}
          className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${
            promiseDates[group.customerId] && !promiseSaving[group.customerId]
              ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {promiseSaving[group.customerId] ? 'กำลังบันทึก...' : '📌 บันทึกนัด'}
        </button>
      </div>
      <p className="text-xs text-gray-400 mt-1">ลูกค้าจะโผล่ในหน้า "โทรตาม" โดยอัตโนมัติหลังจากเลยวันนัด 1 วัน</p>
    </div>
  );

  // ─── Sort helpers ────────────────────────────────────────────────────────
  // สีแดง (highlight / isOverdue / fromFinalNotice) ขึ้นก่อนเสมอ
  const sortedGroups = [...customerGroups].sort((a, b) => {
    const aUrgent = a.highlight || a.isOverdue || a.fromFinalNotice || false;
    const bUrgent = b.highlight || b.isOverdue || b.fromFinalNotice || false;
    if (aUrgent && !bUrgent) return -1;
    if (!aUrgent && bUrgent) return 1;
    return 0;
  });

  // ─── Shared orders table ──────────────────────────────────────────────────
  const renderOrdersTable = (orders) => (
    <table className="min-w-full">
      <thead>
        <tr className="bg-gray-100 text-sm">
          <th className="px-5 py-2 text-left font-semibold text-gray-700">วันที่สั่ง</th>
          <th className="px-5 py-2 text-left font-semibold text-gray-700">Link</th>
          <th className="px-5 py-2 text-right font-semibold text-gray-700">จำนวน (เยน)</th>
          <th className="px-5 py-2 text-right font-semibold text-gray-700">ค่าส่ง JP</th>
          <th className="px-5 py-2 text-right font-semibold text-gray-700">Rate</th>
          <th className="px-5 py-2 text-right font-semibold text-gray-700">ยอดรวม (บาท)</th>
        </tr>
      </thead>
      <tbody>
        {orders.map((order) => {
          const orderTotal = (order.amount * order.qty + (order.deliveryFeeJp || 0)) * order.productRate;
          return (
            <tr key={order.id} className="border-t border-gray-100 hover:bg-gray-50">
              <td className="px-5 py-2 text-sm">{formatDate(order.orderDate)}</td>
              <td className="px-5 py-2 text-sm">
                <a href={order.url} target="_blank" rel="noopener noreferrer"
                  className="text-blue-600 hover:underline truncate block max-w-xs">
                  {order.url}
                </a>
              </td>
              <td className="px-5 py-2 text-sm text-right">{order.amount * order.qty}</td>
              <td className="px-5 py-2 text-sm text-right">{order.deliveryFeeJp ?? '-'}</td>
              <td className="px-5 py-2 text-sm text-right">{order.productRate}</td>
              <td className="px-5 py-2 text-sm text-right font-medium">{formatAmountRound(orderTotal)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div>
      <h2 className="text-3xl font-bold mb-4">ติดตามค้างจ่าย</h2>

      {/* Tabs */}
      <div className="flex mb-6 border-b flex-wrap">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`px-5 py-3 mr-1 font-medium text-base transition-colors flex items-center gap-1 ${
              activeTab === tab.id
                ? 'border-b-2 border-blue-500 text-blue-500'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-600'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {isLoading && <div className="text-center py-8 text-gray-500">กำลังโหลด...</div>}
      {!isLoading && customerGroups.length === 0 && (
        <div className="text-center py-8 text-gray-400 text-lg">ไม่มีรายการ</div>
      )}

      {/* ── Tab 1–4: ทวงครั้งที่ 2/3, Final Notice, ส่งเอกสาร ── */}
      {!isLoading && [1, 2, 3, 4].includes(activeTab) && customerGroups.length > 0 && (
        <div className="space-y-6">
          {sortedGroups.map((group) => (
            <div key={group.customerId}
              className={`border rounded-lg overflow-hidden ${
                group.highlight ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'
              }`}>
              <div className={`flex items-center justify-between px-5 py-3 ${group.highlight ? 'bg-red-100' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="font-bold text-lg">{group.contact}</span>
                  {group.smileName && <span className="text-gray-500">({group.smileName})</span>}
                  <span className="font-semibold text-blue-600">
                    รวม {formatAmountRound(calculateGroupTotal(group.orders))} บาท
                  </span>
                  {group.highlight && (
                    <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">เลยกำหนด</span>
                  )}
                  {activeTab > 1 && group.previousStepProcessedAt && (
                    <span className="text-sm text-gray-600">
                      วันที่ทำขั้นก่อนหน้า: {formatDate(group.previousStepProcessedAt)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {activeTab === 1 && (
                    <button onClick={() => handleMarkCall(group)}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md font-medium transition-colors">
                      โทรตาม
                    </button>
                  )}
                  {activeTab === 4 && (
                    <>
                      <button
                        onClick={() => handleOpenLetter(group)}
                        disabled={!!letterLoading[group.customerId]}
                        className="bg-red-700 hover:bg-red-800 disabled:opacity-50 text-white px-4 py-2 rounded-md font-medium transition-colors">
                        {letterLoading[group.customerId] ? 'กำลังโหลด...' : '📄 สร้างจดหมาย PDF'}
                      </button>
                      <button
                        onClick={() => setPaymentHistoryProfile({ id: group.customerId })}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md font-medium transition-colors">
                        ประวัติการจ่าย
                      </button>
                      {/* Tracking number — required before เสร็จสิ้น */}
                      <input
                        type="text"
                        placeholder="เลขพัสดุ *"
                        className="border border-gray-300 rounded px-3 py-2 text-sm w-40 focus:outline-none focus:ring-2 focus:ring-green-400"
                        value={trackingNumbers[group.customerId] || ''}
                        onChange={(e) => setTrackingNumbers(prev => ({ ...prev, [group.customerId]: e.target.value.trim() }))}
                      />
                    </>
                  )}
                  {activeTab !== 4 && (
                    <button onClick={() => handleCopy(group)}
                      disabled={!!copyInProgress[group.customerId]}
                      className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md font-medium transition-colors">
                      {copiedContacts[group.customerId] ? 'คัดลอกแล้ว ✓' : (copyInProgress[group.customerId] ? 'กำลังสร้างลิงก์...' : 'คัดลอกข้อความ')}
                    </button>
                  )}
                  <button onClick={() => handleDone(group)}
                    disabled={
                      (activeTab !== 4 && !copiedContacts[group.customerId]) ||
                      (activeTab === 4 && !trackingNumbers[group.customerId])
                    }
                    className={`px-4 py-2 rounded-md font-medium transition-colors ${
                      (activeTab !== 4 && copiedContacts[group.customerId]) ||
                      (activeTab === 4 && trackingNumbers[group.customerId])
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}>
                    เสร็จสิ้น
                  </button>
                </div>
              </div>
              {/* บันทึกนัดชำระ: Tab 2 (ทวงครั้งที่ 3) และ Tab 3 (Final Notice) เท่านั้น */}
              {[2, 3].includes(activeTab) && renderPromiseSection(group)}
              {renderOrdersTable(group.orders)}
            </div>
          ))}
        </div>
      )}

      {/* ── Tab 5: โทรตาม ── */}
      {!isLoading && activeTab === 5 && customerGroups.length > 0 && (
        <div className="space-y-6">
          {sortedGroups.map((group) => {
            const grandTotal = calculateGroupTotal(group.orders);
            return (
              <div key={group.callFollowUpId}
                className="border border-gray-200 bg-white rounded-lg overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 bg-gray-50">
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="font-bold text-lg">{group.contact}</span>
                    {group.smileName && <span className="text-gray-500">({group.smileName})</span>}
                    <span className="font-semibold text-blue-600">
                      รวม {formatAmountRound(grandTotal)} บาท
                    </span>
                    {group.phone ? (
                      <a href={`tel:${group.phone}`}
                        className="flex items-center gap-1 text-green-700 font-semibold bg-green-100 px-3 py-1 rounded-full hover:bg-green-200 transition-colors">
                        📞 {group.phone}
                      </a>
                    ) : (
                      <span className="text-gray-400 text-sm">ไม่มีเบอร์โทร</span>
                    )}
                    {group.fromFinalNotice && (
                      <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full font-bold">
                        ‼️ Final Notice แล้ว
                      </span>
                    )}
                    {group.note && (
                      <button
                        onClick={() => setNoteOpen(prev => ({ ...prev, [group.callFollowUpId]: !prev[group.callFollowUpId] }))}
                        className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium hover:bg-yellow-200 transition-colors"
                      >
                        📝 Note
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {group.fromFinalNotice ? (
                      // Already had Final Notice → offer to skip straight to ส่งเอกสาร
                      <button
                        onClick={() => handlePushToFinalNotice(group, 3)}
                        className="bg-gray-800 hover:bg-black text-white px-4 py-2 rounded-md font-medium transition-colors text-sm">
                        📄 ส่งจดหมาย
                      </button>
                    ) : (
                      // Not yet → send to Final Notice first
                      <button
                        onClick={() => handlePushToFinalNotice(group, 2)}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium transition-colors text-sm">
                        ‼️ ส่ง Final Notice
                      </button>
                    )}
                    <button
                      onClick={() => setPaymentHistoryProfile({ id: group.customerId })}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md font-medium transition-colors text-sm">
                      ประวัติการจ่าย
                    </button>
                  </div>
                </div>
                {/* Note display */}
                {group.note && noteOpen[group.callFollowUpId] && (
                  <div className="px-5 py-2 bg-yellow-50 border-b border-yellow-200 text-sm text-yellow-900">
                    <span className="font-semibold">📝 Note: </span>{group.note}
                  </div>
                )}
                {/* Advance amount */}
                {renderAdvanceAmount(grandTotal, group.advanceAmount)}
                {/* Call info + date picker + เสร็จสิ้น */}
                <div className="px-5 py-3 bg-orange-50 border-b border-orange-100 flex items-center gap-4 flex-wrap text-sm">
                  <span className="text-gray-600">
                    📅 โทรครั้งล่าสุด: <span className="font-semibold text-gray-800">{formatDate(group.lastCallDate)}</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700 font-medium">🗓 ลูกค้านัดชำระวันที่:</span>
                    <input
                      type="date"
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                      value={callDates[group.callFollowUpId] || ''}
                      onChange={(e) => setCallDates(prev => ({ ...prev, [group.callFollowUpId]: e.target.value }))}
                    />
                    <button
                      onClick={() => handleMoveToWaiting(group)}
                      disabled={!callDates[group.callFollowUpId]}
                      className={`px-4 py-1.5 rounded font-medium text-sm transition-colors ${
                        callDates[group.callFollowUpId]
                          ? 'bg-green-600 hover:bg-green-700 text-white'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}>
                      เสร็จสิ้น
                    </button>
                  </div>
                </div>
                {renderOrdersTable(group.orders)}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Tab 6: โทรตาม (รอติดตามผล) ── */}
      {!isLoading && activeTab === 6 && customerGroups.length > 0 && (
        <div className="space-y-6">
          {sortedGroups.map((group) => {
            const isOverdue = group.isOverdue;
            const grandTotal = calculateGroupTotal(group.orders);
            const cardClass = isOverdue
              ? 'border-red-400 bg-red-50'
              : 'border-yellow-300 bg-yellow-50';
            const headerClass = isOverdue ? 'bg-red-100' : 'bg-yellow-100';
            const infoClass = isOverdue ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200';

            return (
              <div key={group.callFollowUpId} className={`border rounded-lg overflow-hidden ${cardClass}`}>
                {/* Header */}
                <div className={`flex items-center justify-between px-5 py-3 ${headerClass}`}>
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="font-bold text-lg">{group.contact}</span>
                    {group.smileName && <span className="text-gray-500">({group.smileName})</span>}
                    <span className="font-semibold text-blue-600">
                      รวม {formatAmountRound(grandTotal)} บาท
                    </span>
                    {group.phone ? (
                      <a href={`tel:${group.phone}`}
                        className="flex items-center gap-1 text-green-700 font-semibold bg-green-100 px-3 py-1 rounded-full hover:bg-green-200 transition-colors">
                        📞 {group.phone}
                      </a>
                    ) : (
                      <span className="text-gray-400 text-sm">ไม่มีเบอร์โทร</span>
                    )}
                    {group.postponeCount > 0 && (
                      <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                        isOverdue ? 'bg-red-500 text-white' : 'bg-yellow-500 text-white'
                      }`}>
                        เลื่อนมาแล้ว {group.postponeCount} ครั้ง
                      </span>
                    )}
                    {isOverdue && (
                      <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                        เลยวันนัด
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPaymentHistoryProfile({ id: group.customerId })}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md font-medium transition-colors text-sm">
                      ประวัติการจ่าย
                    </button>
                  </div>
                </div>
                {/* Advance amount */}
                {renderAdvanceAmount(grandTotal, group.advanceAmount)}
                {/* Info row */}
                <div className={`px-5 py-3 border-b ${infoClass} flex items-center gap-6 flex-wrap text-sm`}>
                  <span className="text-gray-600">
                    📅 โทรครั้งล่าสุด: <span className="font-semibold text-gray-800">{formatDate(group.lastCallDate)}</span>
                  </span>
                  <span className={`font-semibold ${isOverdue ? 'text-red-700' : 'text-yellow-700'}`}>
                    🗓 นัดชำระ: {group.promisedPayDate ? group.promisedPayDate : '-'}
                    {isOverdue ? ' ⚠️ เลยกำหนด' : ' (ยังไม่ถึงกำหนด)'}
                  </span>
                  {/* เลื่อนชำระ */}
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700 font-medium">เลื่อนชำระ:</span>
                    <input
                      type="date"
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                      value={postponeDates[group.callFollowUpId] || ''}
                      onChange={(e) => setPostponeDates(prev => ({ ...prev, [group.callFollowUpId]: e.target.value }))}
                    />
                    <button
                      onClick={() => handlePostpone(group)}
                      disabled={!postponeDates[group.callFollowUpId] || !!postponeSaving[group.callFollowUpId]}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                        postponeDates[group.callFollowUpId] && !postponeSaving[group.callFollowUpId]
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}>
                      {postponeSaving[group.callFollowUpId] ? 'กำลังบันทึก...' : 'บันทึก'}
                    </button>
                  </div>
                </div>
                {renderOrdersTable(group.orders)}
              </div>
            );
          })}
        </div>
      )}

      {/* Payment History Modal */}
      {paymentHistoryProfile && (
        <CustomerPaymentHistoryModal
          isOpen={!!paymentHistoryProfile}
          onClose={() => setPaymentHistoryProfile(null)}
          profile={paymentHistoryProfile}
        />
      )}

      {/* Debt Letter Modal */}
      <DebtLetterModal
        isOpen={letterModalOpen}
        onClose={() => { setLetterModalOpen(false); setLetterData(null); }}
        letterData={letterData}
      />
    </div>
  );
};

export default DebtFollowUp;
